"use client";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Rating from "@mui/material/Rating";
import Typography from "@mui/material/Typography";
import React, { ChangeEvent, useEffect, useState } from "react";
import useCart from "../../salify-next-layout/default/hooks/useCart";
import { useRouter } from "next/navigation";
import ReactImageMagnify from "react-image-magnify";
import { Product } from "src/lib/fetcher/client";
import { getImageMain, getImageZoom, toAssetImages } from "src/lib/utils";
import { formatMoney } from "src/lib/utils/formatter";
import IconDefault from "../../icon/default";
import SalifyFallback from "../../salify-fallback/default";
import { H1, H3 } from "../../salify-next-layout/default/components/Typography";
import { FlexBox, FlexRowCenter } from "../../salify-next-layout/default/components/flex-box";
import AddToCartButtons from "./add-to-cart";
import QuantityButtons from "./quantity-buttons";
import { MobileStyledBox } from "./styles";
import ProductAttributes from "./product-attributes";

// ================================================================
const ProductIntro: React.FC<{
  product: Product;
  brandName: string;
}> = ({ product, brandName }) => {
  const { state, dispatch } = useCart();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectionAttribute, setSelectionAttribute] = useState<any>({});
  const [selectionProduct, setSelectionProduct] = useState(product);

  const sortedMedias = selectionProduct.medias?.toSorted(
    (a, b) =>
      +(new RegExp(/.*-(\d+)\..+/).exec(a.value)?.[1] ?? 0) -
      +(new RegExp(/.*-(\d+)\..+/).exec(b.value)?.[1] ?? 0),
  );

  const products: any[] = [];
  products.push(product);

  var grpAttrs: any = {};
  product.attributes.forEach((item) => {
    if (grpAttrs[item.name] === undefined) {
      grpAttrs[item.name] = [];
    }
    grpAttrs[item.name].push(item.value);
  });
  product.variants.forEach((item) => {
    products.push(item);
    item.attributes?.forEach((attr: any) => {
      if (grpAttrs[attr.name] === undefined) {
        grpAttrs[attr.name] = [];
      }
      if (grpAttrs[attr.name].indexOf(attr.value) === -1) {
        grpAttrs[attr.name].push(attr.value);
      }
    });
  });

  const router = useRouter();

  const cartItem = state.cart.find((item) => item.product.id === selectionProduct.id); // HANDLE CHANGE CART

  const attrNames = product.attributes.map((x) => x.name);

  useEffect(() => {
    setQuantity(() => cartItem?.quantity ?? 1);
  }, [cartItem?.quantity]);

  useEffect(() => {
    if (Object.keys(selectionAttribute).length === 0) {
      return;
    }
    products.forEach((product) => {
      let isMatch = true;
      attrNames.forEach((name) => {
        var attr = product.attributes.find((x: any) => x.name === name).value ?? undefined;
        var attr2 = selectionAttribute[name];
        if (attr2 !== undefined && attr !== attr2) {
          isMatch = false;
        }
      });

      if (isMatch) {
        setSelectionProduct(product);
        return;
      }
    });
  }, [selectionAttribute]);

  const handleImageClick = (ind: number) => () => setSelectedImage(ind); // HANDLE SELECT IMAGE

  const handleCartAmountChange = (quantity: number, options?: { goToCart?: boolean }) => () => {
    dispatch({
      type: "CHANGE_CART_AMOUNT",
      payload: {
        product: {
          ...selectionProduct,
          productId: selectionProduct.id,
          total: quantity * selectionProduct.originalSalePrice,
        },
        quantity,
      },
    });
    if (options?.goToCart) {
      router.push("/cart/review");
    }
  };

  return (
    <Box width="100%">
      <Grid container spacing="20px">
        {/* IMAGE GALLERY AREA */}
        <Grid item md={6} xs={12} alignItems="center" display="flex" flexDirection="column">
          <FlexBox justifyContent="center" mb={1.25}>
            <ReactImageMagnify
              {...{
                smallImage: {
                  alt: selectionProduct.name,
                  isFluidWidth: false,
                  src: getImageMain(sortedMedias?.[selectedImage]?.value ?? ""),
                  width: 480,
                  height: 480,
                },
                largeImage: {
                  alt: selectionProduct.name,
                  src: getImageZoom(sortedMedias?.[selectedImage]?.value ?? ""),
                  width: 960,
                  height: 960,
                },
                enlargedImageContainerStyle: {
                  zIndex: 1100,
                  backgroundColor: "white",
                },
                enlargedImageStyle: {
                  maxWidth: "unset",
                },
              }}
            />
          </FlexBox>

          <FlexBox overflow="auto">
            {sortedMedias
              ?.toSorted(
                (a, b) =>
                  +(new RegExp(/.*-(\d+)\..+/).exec(a.value)?.[1] ?? 0) -
                  +(new RegExp(/.*-(\d+)\..+/).exec(b.value)?.[1] ?? 0),
              )
              ?.map(({ id, value: url }, ind) => (
                <FlexRowCenter
                  key={id}
                  width={64}
                  height={64}
                  minWidth={64}
                  bgcolor="white"
                  border="1px solid"
                  borderRadius="10px"
                  ml={ind === 0 ? "auto" : 0}
                  style={{
                    cursor: "pointer",
                  }}
                  onClick={handleImageClick(ind)}
                  mr={ind === sortedMedias.length - 1 ? "auto" : "10px"}
                  borderColor={selectedImage === ind ? "primary.main" : "grey.400"}
                >
                  <Avatar
                    alt="product"
                    src={toAssetImages(url)}
                    variant="square"
                    sx={{
                      height: 40,
                    }}
                  />
                </FlexRowCenter>
              ))}
          </FlexBox>
        </Grid>

        {/* PRODUCT INFO AREA */}
        <Grid item md={6} xs={12} alignItems="center">
          <FlexBox flexDirection="column" gap="20px">
            {/* PRODUCT NAME */}
            <Box>
              <Typography component={H1} fontWeight={500} fontSize={24}>
                {selectionProduct.name || "Sản phẩm"}
              </Typography>

              {/* PRODUCT RATING */}
              <FlexBox alignItems="center" gap={0.75} mt={1}>
                <Rating
                  size="small"
                  color="warn"
                  value={selectionProduct.finalRating}
                  precision={0.5}
                  readOnly
                />
                <span>{selectionProduct.finalRating.toFixed(1)}</span>
                <Box lineHeight="1">
                  <SalifyFallback.LabelTemplate
                    path="Total Rating"
                    default="({{totalRating}} đánh giá)"
                    params={{
                      totalRating: selectionProduct.totalRating ?? 0,
                    }}
                  />
                </Box>
                |
                <Box color="dark.dark3">
                  <IconDefault icon="mdi:basket-outline" fontSize={16} />
                </Box>
                <SalifyFallback.LabelTemplate
                  path="Total Sale"
                  default="Đã bán: {{totalSale}} lượt"
                  params={{
                    totalSale: selectionProduct.totalSale ?? 0,
                  }}
                />
              </FlexBox>
            </Box>
            <Divider />

            {/* PRODUCT BRAND */}
            <FlexBox alignItems="center">
              <Typography color="dark.dark3">
                <SalifyFallback.Label path="Brand" default="Thương hiệu" />
              </Typography>
              &nbsp;
              <span>{selectionProduct.brand?.name ?? brandName}</span>
            </FlexBox>

            {/* PRICE & STOCK */}
            <FlexBox gap={0.75} alignItems="center">
              <Typography
                component={H3}
                fontSize={20}
                fontWeight={600}
                color="custom.orange"
                lineHeight="1"
              >
                {formatMoney(selectionProduct.salePrice)}
              </Typography>
              <Typography color="dark.dark3" sx={{ textDecoration: "line-through" }}>
                {formatMoney(selectionProduct.originalSalePrice)}
              </Typography>
              {!!selectionProduct.saleOffPercent && (
                <Box
                  sx={{
                    bgcolor: "light.light1",
                    color: "dark.dark3",
                    borderRadius: 0.5,
                    p: "2px 3px",
                    fontSize: 12,
                  }}
                >{`-${selectionProduct.saleOffPercent}%`}</Box>
              )}
            </FlexBox>

            {/*Attrs*/}

            <ProductAttributes
              names={attrNames}
              values={grpAttrs}
              onClick={setSelectionAttribute}
            ></ProductAttributes>

            <Box display={{ xs: "none", md: "block" }} gap={1}>
              <QuantityButtons
                quantity={quantity}
                handleDecrement={() => setQuantity((prev) => (prev > 0 ? prev - 1 : 0))}
                handleIncrement={() => setQuantity((prev) => prev + 1)}
                handleEdit={(e: ChangeEvent<HTMLInputElement>) => {
                  setQuantity(() => (+e.target.value > 0 ? +e.target.value : 0));
                }}
              />

              {/* ADD TO CART BUTTON */}
              <Box mt="45px">
                <AddToCartButtons
                  onBuyNow={handleCartAmountChange(quantity, { goToCart: true })}
                  onAddToCart={handleCartAmountChange(quantity)}
                  isDisabledActions={quantity <= 0 || cartItem?.quantity === quantity}
                />
              </Box>
            </Box>

            <MobileStyledBox display={{ xs: "flex", md: "none" }} gap="10px">
              <QuantityButtons
                quantity={quantity}
                handleDecrement={() => setQuantity((prev) => (prev > 0 ? prev - 1 : 0))}
                handleIncrement={() => setQuantity((prev) => prev + 1)}
                handleEdit={(e: ChangeEvent<HTMLInputElement>) => {
                  setQuantity(() => (+e.target.value > 0 ? +e.target.value : 0));
                }}
              />

              {/* ADD TO CART BUTTON */}
              <AddToCartButtons
                onBuyNow={handleCartAmountChange(quantity, { goToCart: true })}
                onAddToCart={handleCartAmountChange(quantity)}
                isDisabledActions={quantity <= 0 || cartItem?.quantity === quantity}
              />
            </MobileStyledBox>
            {/* SHOP NAME */}
            {/* TODO show when prop shop is available */}
            {/* <FlexBox alignItems="center" gap={1} mb={2}>
            <div>
              <SalifyFallback.Label path="Sold By" default="Được bán bởi" />:
            </div>
            <SalifyLink href={product.shop.link}>
              <H6>{product.shop.name}</H6>
            </SalifyLink>
          </FlexBox> */}
          </FlexBox>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductIntro;
